import React, { useState } from "react";
import { buildTargetUrl, isChildActive, isExactMatch } from "./utils";
import { NestedGroup } from "./NestedGroup";
import { SubItem } from "./SubItem";
import "./sidebarGroup.scss";
import {
  alpha,
  Box,
  ButtonBase,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router";
import { ArrowDown2 } from "../../../../components/Icon";
import Icon from "../../../../components/Icon/Icon";
/** Main Component
 * Item containing submenu
 */
export const SidebarGroup = ({
  item,
  open,
  isActive,
  expandedGroups,
  toggleGroup,
  contentVisible,
  setOpenMenu,
  index,
  showMenu,
  level = 0,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isOpen = expandedGroups[`level${level}`] === item.segment;

  const [openSubmenu, setOpenSubmenu] = useState(null);

  const showSubMenu = (key) => openSubmenu === key;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        ...(isActive(item.segment) && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: open ? 1.9 : 3.5,
            width: 4,
            bgcolor: theme.palette.primary.main,
            borderRadius: "0 4px 4px 0",
            height: open ? 34 : 39,
          },
        }),
      }}
    >
      {open ? (
        contentVisible && (
          <>
            <ListItemButton
              onClick={() => toggleGroup(item.segment, level)}
              selected={isActive(item.segment)}
              sx={{
                minHeight: 38,
                justifyContent: "initial",
                mx: 1,
                py: 0,
                borderRadius: isOpen ? "8px 8px 0 0" : 2,
                bgcolor: isOpen && alpha(theme.palette.paleBlue2.main, 0.7),
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
                <Icon
                  name={item.iconName}
                  color={
                    isActive(item.segment)
                      ? theme.palette.primary.main
                      : theme.palette.secondary.main
                  }
                />
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  color: isActive(item.segment) ? "primary" : "secondary",
                  fontWeight: 600,
                  fontSize: 12.5,
                }}
              />
              <ArrowDown2
                className="submenu_arrow_collapse"
                size={15}
                style={{
                  transform: isOpen ? "rotate(-180deg)" : "rotate(0deg)",
                }}
                color={
                  isActive(item.segment)
                    ? theme.palette.primary.main
                    : theme.palette.secondary.main
                }
              />
            </ListItemButton>

            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List
                disablePadding
                sx={{
                  bgcolor: alpha(theme.palette.paleBlue2.main, 0.7),
                  mx: 1,
                  pb: 0.5,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                }}
              >
                {item.children.map((child, i) =>
                  child.kind === "group" ? (
                    <NestedGroup
                      key={i}
                      child={child}
                      level={level + 1}
                      open={open}
                      expandedGroups={expandedGroups}
                      toggleGroup={toggleGroup}
                      navigate={navigate}
                      location={location}
                      theme={theme}
                    />
                  ) : (
                    <SubItem
                      key={i}
                      child={child}
                      open={open}
                      level={level + 1}
                      navigate={navigate}
                      location={location}
                      theme={theme}
                    />
                  )
                )}
              </List>
            </Collapse>
          </>
        )
      ) : (
        <>
          <ButtonBase
            onClick={() =>
              setOpenMenu((prev) => (prev === index ? null : index))
            }
            onMouseEnter={() => setOpenMenu(index)}
            onMouseLeave={() => setOpenMenu(null)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 0.75,
              width: "85%",
              mx: "auto",
              ml: 0.75,
              borderRadius: 2,
              cursor: "pointer",
              transition: "background 0.3s",
              position: "relative",
              bgcolor: isActive(item.segment) && theme.palette.paleBlue2.main,
              height: 48,
              "&:hover": {
                bgcolor: theme.palette.paleBlue.main,
              },
            }}
            focusRipple
          >
            <Icon
              name={item.iconName}
              color={
                isActive(item.segment)
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main
              }
            />
            <Typography
              variant="caption"
              fontSize={10}
              fontWeight={600}
              textAlign="center"
              color={isActive(item.segment) ? "primary" : "secondary"}
            >
              {item.title}
            </Typography>
          </ButtonBase>

          {showMenu(index) && (
            <Box
              onMouseEnter={() => setOpenMenu(index)}
              onMouseLeave={() => {
                setOpenMenu(null);
                setOpenSubmenu(null);
              }}
              sx={{
                position: "absolute",
                top: -10,
                left: "85%",
                minWidth: 150,
                p: 1,
              }}
            >
              <Paper
                elevation={2}
                sx={{
                  px: 0.5,
                  borderRadius: 2,
                }}
              >
                <List>
                  {item.children.map((child, i) => {
                    const active =
                      isExactMatch(
                        location,
                        child.segment,
                        child.query,
                        child.activeMatch
                      ) || isChildActive(child, location);

                    if (child.kind === "item") {
                      return (
                        <ListItemButton
                          key={i}
                          selected={active}
                          onClick={() => {
                            navigate(
                              buildTargetUrl(child.segment, child.query)
                            );
                            setOpenMenu(null);
                          }}
                          sx={{
                            borderRadius: 2,
                            mb: 0.3,
                            bgcolor: active
                              ? alpha(theme.palette.common.white, 0.9)
                              : "transparent",
                          }}
                        >
                          <ListItemText
                            primary={child.title}
                            primaryTypographyProps={{
                              fontWeight: 600,
                              fontSize: 12,
                              color: active ? "primary" : "secondary",
                            }}
                          />
                        </ListItemButton>
                      );
                    }

                    if (child.kind === "group") {
                      return (
                        <Box key={i} sx={{ position: "relative" }}>
                          <ListItemButton
                            selected={active}
                            onClick={() =>
                              setOpenSubmenu((prev) =>
                                prev === child.segment ? null : child.segment
                              )
                            }
                            onMouseEnter={() => {
                              setOpenMenu(index);
                              setOpenSubmenu(child.segment);
                            }}
                            onMouseLeave={() => setOpenSubmenu(null)}
                            sx={{
                              borderRadius: 2,
                              mb: 0.3,
                              bgcolor: active
                                ? alpha(theme.palette.common.white, 0.9)
                                : "transparent",
                            }}
                          >
                            <ListItemText
                              primary={child.title}
                              primaryTypographyProps={{
                                fontWeight: 600,
                                fontSize: 12,
                                color: active ? "primary" : "secondary",
                                mr: 1,
                              }}
                            />
                            <ArrowDown2
                              size={14}
                              style={{
                                transform: showSubMenu(child.segment)
                                  ? "rotate(-90deg)"
                                  : "rotate(0deg)",
                              }}
                              color={
                                active
                                  ? theme.palette.primary.main
                                  : theme.palette.secondary.main
                              }
                            />
                          </ListItemButton>

                          {showSubMenu(child.segment) && (
                            <Box
                              onMouseEnter={() => {
                                setOpenMenu(index);
                                setOpenSubmenu(child.segment);
                              }}
                              onMouseLeave={() => setOpenSubmenu(null)}
                              sx={{
                                position: "absolute",
                                top: -10,
                                p: 1,
                                left: "97.5%",
                                minWidth: 150,
                              }}
                            >
                              <Paper
                                elevation={3}
                                sx={{
                                  borderRadius: 2,
                                  px: 0.5,
                                }}
                              >
                                <List>
                                  {child.children.map((sub, j) => {
                                    const subActive = isExactMatch(
                                      location,
                                      sub.segment,
                                      sub.query,
                                      sub.activeMatch
                                    );

                                    return (
                                      <ListItemButton
                                        key={j}
                                        selected={subActive}
                                        onClick={() => {
                                          navigate(
                                            buildTargetUrl(
                                              sub.segment,
                                              sub.query
                                            )
                                          );
                                          setOpenSubmenu(null);
                                          setOpenMenu(null);
                                        }}
                                        sx={{
                                          borderRadius: 2,
                                          mb: 0.3,
                                          bgcolor: subActive
                                            ? alpha(
                                                theme.palette.common.white,
                                                0.9
                                              )
                                            : "transparent",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 4,
                                            height: 4,
                                            borderRadius: "50%",
                                            bgcolor: subActive
                                              ? theme.palette.primary.main
                                              : theme.palette.secondary.main,
                                            flexShrink: 0,
                                          }}
                                        />
                                        <ListItemText
                                          primary={sub.title}
                                          primaryTypographyProps={{
                                            fontWeight: 500,
                                            fontSize: 11.5,
                                            mx: 0.5,
                                            color: subActive
                                              ? "primary"
                                              : "secondary",
                                          }}
                                        />
                                      </ListItemButton>
                                    );
                                  })}
                                </List>
                              </Paper>
                            </Box>
                          )}
                        </Box>
                      );
                    }
                    return null;
                  })}
                </List>
              </Paper>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
